/**
 * TestSendDialog.tsx — 推送模板测试发送弹窗（ADM-PUSH-S006，dev-docs `02-API契约/A4-运营推送.md` §7）
 *
 * 外壳与信息条沿用原型 design/Novel-admin/src/dialogs2.jsx 2123–2186；字段按契约：目标 App + 收件人 user_id
 * + 该 slug 白名单内的占位符取值。原型的「语言版本」选择不进请求 —— 渲染语言由服务端按收件人最近使用语言决定
 * （`90-推送与通知.md` §5.1），这里只说明，不让运营以为能指定语言。
 */

import { PUSH_TEMPLATE_REGISTRY, PUSH_TEMPLATE_TEST_SENDABLE_SLUGS } from '@novel-admin/shared';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { AdminApiError } from '~/api/errors';
import { useApps } from '~/data/queries';
import type { PushTemplate } from '~/data/queries';
import { pushApi } from '~/hooks/use-push';
import { Icons } from '~/lib/icons';
import { Dialog } from '~/ui/Dialog';
import { FormRow } from '~/ui/FormRow';
import { toast } from '~/ui/Toast';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

export function TestSendDialog({
  tpl,
  canWrite,
  onClose,
}: {
  tpl: PushTemplate;
  canWrite: boolean;
  onClose: () => void;
}) {
  const t = tpl;
  const apps = useApps();
  const [appId, setAppId] = useState('');
  const [userId, setUserId] = useState('');
  const placeholders = PUSH_TEMPLATE_REGISTRY[t.slug]?.placeholders ?? [];
  const [params, setParams] = useState<Record<string, string>>({});
  const sendable = PUSH_TEMPLATE_TEST_SENDABLE_SLUGS.includes(t.slug);
  const isMarketing = PUSH_TEMPLATE_REGISTRY[t.slug]?.category === 'OFFER';

  const send = useMutation({
    mutationFn: () =>
      pushApi.testSendTemplate(t.slug, {
        target_app_id: appId,
        target_user_id: userId.trim(),
        // 未填的占位符不上送：服务端按 §5.1 渲染成 {var} 字面，正好暴露漏配
        params: Object.fromEntries(Object.entries(params).filter(([, v]) => v.trim() !== '')),
      }),
    onSuccess: () => {
      toast('已入队 · 约 5 分钟内送达测试设备', { tone: 'pos', icon: 'IconPaperPlane' });
      onClose();
    },
    onError: (error) => {
      toast(error instanceof AdminApiError ? error.message : '测试发送失败', { tone: 'neg' });
    },
  });

  const blockedReason = !canWrite
    ? '权限不足 · 该操作受角色限制（后端 requireRole 为权威）'
    : !sendable
      ? '该模板需要真实业务事件，不支持测试发送'
      : !t.isActive
        ? '模板已停用，启用后才能测试发送'
        : '';
  const ready = blockedReason === '' && appId !== '' && UUID_RE.test(userId.trim());

  return (
    <Dialog
      width={480}
      title="测试发送推送"
      subtitle={`${t.type} · ${t.slug} · 仅内部测试账号可接收`}
      icon="IconPaperPlane"
      onClose={onClose}
      footer={
        <>
          <button className="btn" onClick={onClose}>
            取消
          </button>
          <button
            className="btn btn-accent"
            disabled={!ready || send.isPending}
            title={blockedReason}
            onClick={() => send.mutate()}
          >
            <Icons.IconPaperPlane size={11} />
            {send.isPending ? '发送中…' : '发送测试'}
          </button>
        </>
      }
    >
      <div style={{ padding: 18 }}>
        <FormRow
          label="目标 App"
          required
          hint="收件人所属 App"
          labelWidth={140}
          gap={14}
          pad="12px 0"
          labelPadTop={6}
        >
          <select
            className="input"
            aria-label="目标 App"
            value={appId}
            onChange={(e) => setAppId(e.target.value)}
          >
            <option value="">选择 App</option>
            {apps.data.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
        </FormRow>
        <FormRow
          label="收件人"
          required
          hint="user_id · 须持「内部测试专用账号」标签"
          labelWidth={140}
          gap={14}
          pad="12px 0"
          labelPadTop={6}
        >
          <input
            className="input mono"
            aria-label="收件人 user_id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="0190a290-6f3c-7a11-8b22-334455667788"
            autoFocus
          />
        </FormRow>
        {placeholders.map((name) => (
          <FormRow
            key={name}
            label={`{${name}}`}
            hint="留空则推送里原样显示占位符"
            labelWidth={140}
            gap={14}
            pad="12px 0"
            labelPadTop={6}
          >
            <input
              className="input"
              aria-label={`占位符 ${name}`}
              value={params[name] ?? ''}
              onChange={(e) => setParams((prev) => ({ ...prev, [name]: e.target.value }))}
            />
          </FormRow>
        ))}
        <div
          className="card-flat"
          style={{
            marginTop: 14,
            padding: 10,
            background: 'var(--info-soft)',
            borderLeft: '2px solid var(--info)',
          }}
        >
          <div className="hstack" style={{ gap: 8, alignItems: 'flex-start' }}>
            <Icons.IconInfo
              size={12}
              style={{ color: 'var(--info)', flexShrink: 0, marginTop: 2 }}
            />
            <span style={{ fontSize: 11.5, color: 'var(--fg-muted)', lineHeight: 1.5 }}>
              测试推送不计入正式发送统计 · 文案语言按收件人最近使用的语言选择 · 约 5 分钟内送达
              {isMarketing ? ' · 若测试账号关闭了营销通知则不会收到' : ''}
            </span>
          </div>
        </div>
        {blockedReason && (
          <p style={{ marginTop: 10, fontSize: 11.5, color: 'var(--fg-muted)' }}>{blockedReason}</p>
        )}
      </div>
    </Dialog>
  );
}
